"use client"

import { SearchBar } from "@/components/ui-library-new/search-bar/SearchBar"
import { SearchableSelect } from "@/components/ui-library-new/select/SearchableSelect"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import { VaccinationPriceOverviewSectionStoryblok, VaccineTypeStoryblok } from "@/types/component-types"
import { filterBySearchQuery } from "@/utils/filterBySearchQuery"
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc"
import { useMemo, useState } from "react"
import { ExpandableCardGrid } from "./ExpandableCardGrid"
import { VaccinationPriceCard, type VaccineStory } from "./VaccinationPriceCard"

/** The empty string is the Storyblok default for an unset option, never a type worth filtering on. */
type VaccineType = Exclude<VaccineTypeStoryblok["type"], ""> & string
type VaccineTypeFilter = VaccineType | "risk-group" | "tick" | "tetanus"

const TYPE_FILTERS: { key: string; value: VaccineTypeFilter }[] = [
  { key: "Vaccinationer", value: "normal-vaccine" },
  { key: "Resevaccinationer", value: "travel-vaccine" },
  { key: "Riskgrupp", value: "risk-group" },
  { key: "Fästing", value: "tick" },
  { key: "Stelkramp", value: "tetanus" }
]

/**
 * The vaccines offered to risk groups, by the catalogue `id` an editor maintains on each vaccine
 * config — NOT the Storyblok story id. The comments are the only thing tying an id to a vaccine;
 * nothing matches on slug.
 */
const RISK_GROUP_IDS = new Set([
  3, // influensa
  4, // covid-19
  9, // rs-virus-18plus
  10 // rs-virus-gravid
])

const TICK_IDS = new Set([
  2 // fästing
])

const TETANUS_IDS = new Set([
  5, // stelkramp
  6 // stelkramp
])

export const VaccinationPriceOverviewSection = ({
  blok,
  vaccineTypeFilter
}: BlokProps<VaccinationPriceOverviewSectionStoryblok> & {
  /** The page's own scope — /vaccination/vaccin passes "normal-vaccine" and never shows the rest. */
  vaccineTypeFilter?: VaccineTypeStoryblok["type"]
}) => {
  const { section_title, search_placeholder, price_list_title, items, show_initial_count, use_quick_search } = blok
  const initialCount = show_initial_count || 6
  const [query, setQuery] = useState("")
  // The reader's own choice, layered on top of `filter`. Kept apart from `query` so the two can't
  // overwrite each other — sharing one state put the literal string "normal-vaccine" in the search
  // box and dropped the type the moment anyone typed.
  const [typeFilter, setTypeFilter] = useState<VaccineTypeFilter | null>(null)

  // `items` is a relation field: without `resolveRelations` on the fetch, Storyblok returns bare
  // story UUIDs instead of stories. Rendering silently would hide that, so unresolved items are
  // counted and surfaced below rather than dropped.
  const resolved = useMemo(
    () => (items ?? []).filter((item): item is VaccineStory => typeof item !== "string"),
    [items]
  )
  // Counted before the type filter — otherwise every vaccine the page deliberately hides reads as
  // one that failed to load, and the grid claims a fetch error on an ordinary empty search.
  const unresolvedCount = (items?.length ?? 0) - resolved.length

  const vaccines = useMemo(
    () => (vaccineTypeFilter ? resolved.filter(({ content }) => content.type === vaccineTypeFilter) : resolved),
    [resolved, vaccineTypeFilter]
  )

  const containsBothVaccineTypes = new Set(vaccines.map(({ content }) => content.type)).size > 1

  const filteredItems = useMemo(() => {
    // Risk group cuts across `type` — it is a hand-kept list of vaccines, not a Storyblok field.
    // The predicate has to return a boolean per vaccine: returning the filtered array instead let
    // every item through, since even an empty array is truthy.
    const byType = typeFilter
      ? vaccines.filter(({ content }) => {
          if (["normal-vaccine", "travel-vaccine"].includes(typeFilter)) {
            return content.type === typeFilter
          }
          if (typeFilter === "risk-group") {
            return RISK_GROUP_IDS.has(Number(content.id))
          }
          if (typeFilter === "tick") {
            return TICK_IDS.has(Number(content.id))
          }
          if (typeFilter === "tetanus") {
            return TETANUS_IDS.has(Number(content.id))
          }
          return false
        })
      : vaccines
    // `tag` carries editorial search keywords from the `vaccine-search-tags` datasource, so a
    // reader finds a vaccine by what it protects against ("lunginflammation", "gravid") rather than
    // only by its name. Joined into the same haystack: the tags are not shown anywhere, they only
    // widen what matches.
    return filterBySearchQuery(byType, query, ({ content }) =>
      [content.name, content.description, content.required_amount, content.markers_amount, ...(content.tag ?? [])]
        .filter(Boolean)
        .join(" ")
    )
  }, [vaccines, query, typeFilter])

  const safePlaceholder = search_placeholder?.trim() || "Sök vaccin"
  const isTruncated = filteredItems.length > initialCount

  const card = (story: VaccineStory) => <VaccinationPriceCard key={story.uuid} story={story} />

  const emptyMessage = filteredItems.length === 0 && (
    <p className="col-span-full text-center text-[15px] font-normal leading-[22px] tracking-normal text-neutral-50 lg:text-[16px]">
      {unresolvedCount > 0 ? "Vaccinerna kunde inte laddas." : "Inga vacciner matchar din sökning."}
    </p>
  )

  const gridClassName = "mt-4 lg:mt-6"

  return (
    <section {...storyblokEditable(blok as SbBlokData)} className="bg-surface py-[18px] lg:py-10">
      <div className="mx-auto w-full max-w-[1155px] px-4 lg:px-8">
        <h2 className="text-[18px] font-extrabold text-neutral-90 lg:text-2xl">{section_title}</h2>

        <div className="mt-4 lg:mt-6">
          {containsBothVaccineTypes && use_quick_search && (
            <div className="mb-4 flex items-center gap-2 lg:mb-6">
              <span className="shrink-0 text-sm text-neutral-50 lg:text-base">Snabbfilter:</span>
              {/* One choice at a time, so it says so: checkboxes promised combinations the state
                  never supported — picking a second silently dropped the first. No search box
                  either; five options are quicker to read than to type. */}
              <SearchableSelect
                className="max-w-[16rem] grow"
                searchable={false}
                placeholder="Alla vaccinationer"
                emptyOption="Alla vaccinationer"
                options={TYPE_FILTERS.map(({ key, value }) => ({ value, label: key }))}
                value={typeFilter ?? ""}
                onChange={value => setTypeFilter((value || null) as VaccineTypeFilter | null)}
              />
            </div>
          )}

          <SearchBar value={query} onChange={setQuery} placeholder={safePlaceholder} className="mt-2 lg:mt-4" />
        </div>

        {filteredItems.length > 0 && (
          <p className="mt-4 text-sm text-neutral-50 lg:mt-6 lg:text-base">
            Visar {filteredItems.length} av {vaccines.length}
          </p>
        )}

        {price_list_title && (
          <h3 className="mb-0 mt-8 text-[16px] font-semibold leading-[22px] tracking-[-0.045em] text-neutral-90 lg:mt-12 lg:text-[20px] lg:leading-[27px]">
            {price_list_title}
          </h3>
        )}
        {isTruncated ? (
          // Keyed on the active filters so a new search or type remounts this collapsed — simpler
          // than threading a reset down, and the results underneath have changed anyway.
          <ExpandableCardGrid
            key={`${typeFilter ?? ""}:${query}`}
            className={gridClassName}
            initial={filteredItems.slice(0, initialCount).map(card)}
            rest={filteredItems.slice(initialCount).map(card)}
          />
        ) : (
          <div className={`grid grid-cols-1 gap-6 lg:grid-cols-2 ${gridClassName}`}>
            {emptyMessage}
            {filteredItems.map(card)}
          </div>
        )}
      </div>
    </section>
  )
}
